/**
 * SUNDAY FLOOR — product-form.js
 * Variant switching (price, availability, dimension line, scale note, ATC
 * state, URL) in one operation inside the aria-live region (M5/A12.2).
 * Gallery (desktop thumbs + mobile dots), lightbox, and the mobile sticky
 * ATC bar's show/hide.
 *
 * The product form itself is a plain Shopify `{% form 'product' %}` — a
 * normal POST to /cart/add that works with JS entirely disabled. This file
 * only enhances the experience (live updates, no full reload on variant
 * change); it doesn't implement AJAX add-to-cart or open a cart drawer,
 * since the cart drawer doesn't exist until Stage 5. Submitting the form
 * today does a normal Shopify cart flow.
 */
import { trapFocus, setBackgroundInert } from './global.js';

document.querySelectorAll('[data-pdp-app]').forEach(initPdp);

function initPdp(app) {
  const galleryControls = initGallery(app);
  initVariantPicker(app, galleryControls);
  initLightbox(app);
  initStickyAtc(app);
}

function initVariantPicker(app, galleryControls) {
  const dataScript = app.parentElement.querySelector('[data-variant-data]');
  const combinationScript = app.querySelector('[data-combination-map]');
  if (!dataScript || !combinationScript) return;

  let variantData;
  let combinationMap;
  try {
    variantData = JSON.parse(dataScript.textContent);
    combinationMap = JSON.parse(combinationScript.textContent);
  } catch (err) {
    return; // malformed data — leave the server-rendered state as-is
  }

  const optionGroups = Array.from(app.querySelectorAll('[data-option-group]')).sort(
    (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
  );
  const variantSelect = app.querySelector('[data-variant-select]');
  const priceEl = app.querySelector('[data-pdp-price]');
  const scaleNoteEl = app.querySelector('[data-scale-note]');
  const atcButton = app.querySelector('[data-atc-button]');
  const atcLabel = app.querySelector('[data-atc-label]');

  if (!optionGroups.length || !variantSelect) return;

  // Every real variant's option tuple + availability, derived once from the
  // data already emitted (combinationMap's keys already encode option1/
  // option2/option3 in that fixed order; variantData carries `available`).
  // No separate data pipeline needed for this.
  const allVariants = Object.keys(combinationMap).map((key) => ({
    values: key.split('||'),
    available: Boolean(variantData[combinationMap[key]] && variantData[combinationMap[key]].available),
  }));

  // Generic by design: this never assumes which options exist, how many
  // there are, or what they're named. It builds a fixed 3-slot key
  // (Shopify products have a hard 3-option maximum) from whichever radio
  // is checked in each option group, keyed by that group's data-option-position
  // (1/2/3) — empty string for any position this product doesn't use —
  // matching exactly how Liquid serialized option1/option2/option3 into
  // the combination map above.
  function currentCombinationKey() {
    const slots = ['', '', ''];
    optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      const checked = group.querySelector('[data-option-radio]:checked');
      if (position >= 1 && position <= 3) {
        slots[position - 1] = checked ? checked.value : '';
      }
    });
    return slots.join('||');
  }

  // Shopify's own documented "top-down" model: an option value's
  // availability is constrained only by whatever is currently selected in
  // the option groups *before* it (lower position number), never by ones
  // after it. This is exactly what product_option_value.available computes
  // server-side for the page's initial selection — this reproduces the
  // same rule client-side so it can be recalculated after every change,
  // instead of staying frozen at whatever the first render happened to be.
  // Position order is read from data-option-position; nothing here assumes
  // a name like "Color" or "Size", or that there are exactly two options.
  function isValueAvailable(position, candidateValue, selectedByPosition) {
    return allVariants.some((variant) => {
      if (!variant.available) return false;
      if (variant.values[position - 1] !== candidateValue) return false;
      for (let ancestorPosition = 1; ancestorPosition < position; ancestorPosition += 1) {
        const selectedAtAncestor = selectedByPosition[ancestorPosition];
        if (selectedAtAncestor && variant.values[ancestorPosition - 1] !== selectedAtAncestor) {
          return false;
        }
      }
      return true;
    });
  }

  function recalculateOptionAvailability() {
    const selectedByPosition = {};
    optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      const checked = group.querySelector('[data-option-radio]:checked');
      selectedByPosition[position] = checked ? checked.value : null;
    });

    optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      group.querySelectorAll('[data-option-radio]').forEach((radio) => {
        // A currently-checked value can still end up disabled here if it's
        // no longer valid under the new context — but it's never
        // force-*unchecked* to silently swap the shopper's selection out
        // from under them. It stays checked-but-disabled, which correctly
        // surfaces as an unresolved combination (ATC disabled,
        // "Unavailable") until the shopper picks a new value.
        const available = isValueAvailable(position, radio.value, selectedByPosition);
        radio.disabled = !available;
        const btn = radio.closest('.sf-option-btn');
        if (btn) btn.classList.toggle('is-unavailable', !available);
      });
    });
  }

  function handleOptionChange() {
    recalculateOptionAvailability();

    const key = currentCombinationKey();
    const variantId = combinationMap[key];
    const data = variantId ? variantData[variantId] : undefined;

    if (!data) {
      // Nonexistent combination: no such variant was ever created in
      // admin. Two independent defenses, one per scenario:
      // - JS enabled: disabling the submit button is what actually blocks
      //   submission here (this select is display:none in JS mode, and a
      //   hidden field's `required` constraint isn't reliably enforced by
      //   browsers, so the disabled button carries the real weight).
      // - JS disabled: the select is the visible, interactive control in
      //   that mode, so clearing its value together with `required` means
      //   there's no selected <option> for the browser to submit at all.
      variantSelect.value = '';
      if (atcButton) atcButton.disabled = true;
      if (atcLabel) {
        atcLabel.textContent = atcLabel.dataset.unavailableLabel || atcLabel.textContent;
      }
      updateOptionButtonSelection(optionGroups);
      disableStickyAtc(app);
      return;
    }

    variantSelect.value = variantId;

    updatePrice(priceEl, data);
    updateScaleNote(scaleNoteEl, data);
    updateAtc(atcButton, atcLabel, data);
    updateOptionButtonSelection(optionGroups);
    updateStickyAtc(app, data);

    // Shopify's native variant->media relationship, nothing inferred from
    // option names/positions. If this variant has no featured media of its
    // own (data.featured_media_id is null), activateByMediaId no-ops and
    // whatever's currently displayed stays put — never a fallback jump to
    // the first image. This is also naturally what makes a Size change
    // within the same Color/Style not disturb the gallery: unless that
    // exact variant has its own distinct media assigned, there's nothing
    // to switch to.
    if (galleryControls) galleryControls.activateByMediaId(data.featured_media_id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  optionGroups.forEach((group) => {
    group.querySelectorAll('[data-option-radio]').forEach((radio) => {
      radio.addEventListener('change', handleOptionChange);
    });
  });

  // Run once immediately so the JS-computed availability is authoritative
  // from the first tick, rather than trusting the server-rendered initial
  // disabled state to already agree with this algorithm. Since both
  // implement the same top-down rule against the same underlying variant
  // data, this should be a no-op visually on a normal load — it only
  // matters once the shopper starts changing options.
  recalculateOptionAvailability();
}

function updateOptionButtonSelection(optionGroups) {
  optionGroups.forEach((group) => {
    group.querySelectorAll('[data-option-radio]').forEach((radio) => {
      const btn = radio.closest('.sf-option-btn');
      if (btn) btn.classList.toggle('is-selected', radio.checked);
    });
  });
}

function updatePrice(priceEl, data) {
  if (!priceEl) return;
  const onSale = data.compare_at_price && data.compare_at_price > data.price;
  priceEl.innerHTML = onSale
    ? `<span class="sf-price sf-price--sale">
        <span class="sf-price__sale">${formatMoney(data.price)}</span>
        <span class="sf-price__compare-at">${formatMoney(data.compare_at_price)}</span>
      </span>`
    : `<span class="sf-price"><span class="sf-price__regular">${formatMoney(data.price)}</span></span>`;
}

// Minimal client-side mirror of the server-side `money` filter: cents to a
// locale-formatted string using the page's own currency, since a full
// Shopify money-format re-implementation isn't worth shipping client-side
// for what is otherwise a same-currency, same-page update.
function formatMoney(cents) {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD',
    }).format(amount);
  } catch (err) {
    return `$${amount.toFixed(2)}`;
  }
}

function updateScaleNote(scaleNoteEl, data) {
  if (!scaleNoteEl) return;
  scaleNoteEl.textContent = data.scale_note || '';
}

function updateAtc(atcButton, atcLabel, data) {
  if (!atcButton) return;
  atcButton.disabled = !data.available;
  if (atcLabel) {
    atcLabel.textContent = data.available
      ? atcLabel.dataset.addLabel || atcLabel.textContent
      : atcLabel.dataset.soldOutLabel || atcLabel.textContent;
  }
}

function updateStickyAtc(app, data) {
  const stickyBar = app.parentElement.querySelector('[data-sticky-atc]');
  if (!stickyBar) return;
  const sizeEl = stickyBar.querySelector('[data-sticky-size]');
  const priceEl = stickyBar.querySelector('[data-sticky-price]');
  const button = stickyBar.querySelector('[data-sticky-atc-button]');
  const label = stickyBar.querySelector('[data-sticky-atc-label]');

  if (sizeEl && data.dim_label_short) sizeEl.textContent = data.dim_label_short;
  if (priceEl) updatePrice(priceEl, data);
  if (button) button.disabled = !data.available;
  if (label) {
    label.textContent = data.available
      ? label.dataset.addLabel || label.textContent
      : label.dataset.soldOutLabel || label.textContent;
  }
}

function disableStickyAtc(app) {
  const stickyBar = app.parentElement.querySelector('[data-sticky-atc]');
  if (!stickyBar) return;
  const button = stickyBar.querySelector('[data-sticky-atc-button]');
  const label = stickyBar.querySelector('[data-sticky-atc-label]');
  if (button) button.disabled = true;
  if (label) label.textContent = label.dataset.unavailableLabel || label.textContent;
}

function initGallery(app) {
  const gallery = app.querySelector('[data-gallery]');
  if (!gallery) return null;

  const main = gallery.querySelector('[data-gallery-main]');
  const slides = gallery.querySelectorAll('[data-gallery-slide]');
  const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
  const dots = gallery.querySelectorAll('[data-gallery-dot]');

  // On mobile, which slide is actually visible is determined by native
  // horizontal scroll position, not the .is-active class (that class only
  // drives desktop's display:none/block toggle and the dot styling). The
  // server correctly marks the variant-assigned slide as .is-active for a
  // deep link (?variant=X) or a non-default initial variant, but the
  // browser still starts a scrollable container at scrollLeft: 0 — so
  // without this, mobile would show the first image regardless of which
  // one the server picked. Instant (not smooth), since this is a load-time
  // correction, not a user-triggered transition.
  const initiallyActive = Array.from(slides).find((slide) => slide.classList.contains('is-active'));
  if (main && initiallyActive && Number(initiallyActive.dataset.index) !== 0) {
    main.scrollTo({ left: initiallyActive.offsetLeft, behavior: 'auto' });
  }

  function setActive(index) {
    slides.forEach((slide) => slide.classList.toggle('is-active', Number(slide.dataset.index) === index));
    thumbs.forEach((thumb) => thumb.classList.toggle('is-active', Number(thumb.dataset.index) === index));
    dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.index) === index));
  }

  // Deliberately separate from setActive rather than folding the scroll
  // into it: setActive also runs from the native-scroll-tracking listener
  // below (mobile swipe), and calling scrollTo from there would mean every
  // scroll tick re-triggers a scroll to the position the user just
  // natively scrolled to — a redundant, fragile feedback loop. This is
  // only called from the variant-change path, where a real programmatic
  // "slide to" is actually wanted.
  function scrollToIndex(index) {
    if (!main) return;
    const targetSlide = Array.from(slides).find((slide) => Number(slide.dataset.index) === index);
    // On desktop .sf-gallery__main has overflow: visible / no scroll
    // container, so this is a harmless no-op there.
    if (targetSlide && typeof main.scrollTo === 'function') {
      main.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
    }
  }

  // Shopify's real media.id, not a position — a variant's featured_media_id
  // is matched against this directly, so the mapping never depends on
  // media order or option names.
  function activateByMediaId(mediaId) {
    if (!mediaId) return; // no featured media on this variant — leave the currently displayed media in place
    const match = Array.from(slides).find((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (!match) return; // defensive: media id not present in this product's own gallery
    const index = Number(match.dataset.index);
    setActive(index);
    scrollToIndex(index);
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => setActive(Number(thumb.dataset.index)));
  });

  // Mobile: keep dots in sync with native scroll-snap position.
  if (main) {
    let ticking = false;
    main.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const index = Math.round(main.scrollLeft / main.clientWidth);
          setActive(index);
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  return { activateByMediaId };
}

function initLightbox(app) {
  const lightbox = app.querySelector('[data-lightbox]');
  if (!lightbox) return;

  const gallery = app.querySelector('[data-gallery]');
  const openTriggers = gallery ? gallery.querySelectorAll('[data-gallery-slide]') : [];
  const closeTargets = lightbox.querySelectorAll('[data-lightbox-close]');
  const images = lightbox.querySelectorAll('[data-lightbox-image]');
  if (!images.length) return; // a product with no media has nothing to open — avoid wiring up a lightbox with no content
  const prevBtn = lightbox.querySelector('[data-lightbox-prev]');
  const nextBtn = lightbox.querySelector('[data-lightbox-next]');

  const state = { removeTrap: () => {}, lastFocused: null, index: 0 };

  function showIndex(index) {
    state.index = ((index % images.length) + images.length) % images.length;
    images.forEach((img) => {
      const isActive = Number(img.dataset.index) === state.index;
      img.classList.toggle('is-active', isActive);
      if (!isActive) img.classList.remove('is-zoomed');
    });
  }

  function open(index) {
    state.lastFocused = document.activeElement;
    showIndex(index);
    lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    setBackgroundInert(lightbox, true);
    state.removeTrap = trapFocus(lightbox);
    const closeBtn = lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (lightbox.hidden) return;
    lightbox.hidden = true;
    document.body.style.overflow = '';
    setBackgroundInert(lightbox, false);
    state.removeTrap();
    document.removeEventListener('keydown', onKeydown);
    if (state.lastFocused) state.lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') showIndex(state.index - 1);
    if (e.key === 'ArrowRight') showIndex(state.index + 1);
  }

  openTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => open(Number(trigger.dataset.index)));
  });
  closeTargets.forEach((el) => el.addEventListener('click', close));
  if (prevBtn) prevBtn.addEventListener('click', () => showIndex(state.index - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => showIndex(state.index + 1));

  images.forEach((img) => {
    img.addEventListener('click', () => img.classList.toggle('is-zoomed'));
  });
}

function initStickyAtc(app) {
  const stickyBar = app.parentElement.querySelector('[data-sticky-atc]');
  const inlineAtc = app.querySelector('[data-atc-button]');
  if (!stickyBar || !inlineAtc) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      // Only reveal once the inline button has scrolled above the viewport
      // (not before initial scroll, and not while it's still visible).
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyBar.hidden = !scrolledPast;
    },
    { threshold: 0 }
  );
  observer.observe(inlineAtc);
}
